import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'

import { pickDetail } from '../lib/data'
// Roles we surface as presets in the dropdown; admins can still type a
// custom role in the modal if they need one we haven't thought of.
const ROLE_PRESETS = ['RDO', 'DM', 'GM', 'AGM', 'VP', 'maintenance_tech', 'controller']

// Scope by role: where the person sits in the hierarchy.
// This determines which of (site_id, district_id, region_id) gets set.
const ROLE_SCOPE = {
  RDO: 'region',
  DM: 'district',
  GM: 'site',
  AGM: 'site',
  VP: 'region',            // treat as region-level too; adjust if company structure differs
  maintenance_tech: 'site',
  controller: 'site',
}
function scopeFor(role) {
  return ROLE_SCOPE[role] || 'site'   // sensible default for custom roles
}

export default function PersonnelManagement() {
  const [personnel, setPersonnel]   = useState([])
  const [regions, setRegions]       = useState([])
  const [districts, setDistricts]   = useState([])
  const [sites, setSites]           = useState([])
  const [profiles, setProfiles]     = useState([])   // for "Link to user" dropdown + linked badge
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [linkFilter, setLinkFilter] = useState('all')  // 'all' | 'linked' | 'unlinked'
  const [editing, setEditing]       = useState(null)   // row or 'new'
  const [inviting, setInviting]     = useState(null)   // personnel row currently being invited
  const [toast, setToast]           = useState(null)
  const flash = msg => { setToast(msg); setTimeout(() => setToast(null), 3500) }

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const [pRes, rRes, dRes, sRes, profRes] = await Promise.all([
      supabase.from('site_personnel')
        .select('*')
        .order('role'),
      supabase.from('regions').select('*').order('code'),
      supabase.from('districts').select('id, district_number, coverage_name, region_id').order('district_number'),
      supabase.from('sites')
        .select('id, name, site_details(site_code)')
        .order('name'),
      // Profiles are used to (a) show a "✓ linked" badge for personnel rows
      // that already point to a profile, (b) power the "Link to existing user"
      // dropdown in the edit modal, and (c) let us detect email collisions
      // before inviting someone whose email already matches an existing user.
      supabase.from('profiles').select('id, full_name, email, role, active').order('full_name'),
    ])
    setPersonnel(pRes.data || [])
    setRegions(rRes.data || [])
    setDistricts(dRes.data || [])
    setSites(sRes.data || [])
    setProfiles(profRes.data || [])
    setLoading(false)
  }

  // Indexes for display — turn FK IDs into human-readable labels.
  const regionById   = useMemo(() => Object.fromEntries(regions.map(r => [r.id, r])), [regions])
  const districtById = useMemo(() => Object.fromEntries(districts.map(d => [d.id, d])), [districts])
  const siteById     = useMemo(() => Object.fromEntries(sites.map(s => [s.id, s])), [sites])
  const profileById  = useMemo(() => Object.fromEntries(profiles.map(p => [p.id, p])), [profiles])
  // Profiles that aren't yet linked to any personnel row — these are the
  // candidates the "Link to existing user" dropdown in the modal shows.
  const linkedProfileIds = useMemo(() => new Set(personnel.map(p => p.profile_id).filter(Boolean)), [personnel])
  const unlinkedProfiles = useMemo(() => profiles.filter(p => !linkedProfileIds.has(p.id) && p.active !== false), [profiles, linkedProfileIds])

  function scopeLabel(p) {
    if (p.region_id)   return `Region ${regionById[p.region_id]?.code || '?'}`
    if (p.district_id) {
      const d = districtById[p.district_id]
      return d ? `District ${d.district_number}${d.coverage_name ? ` — ${d.coverage_name}` : ''}` : 'District ?'
    }
    if (p.site_id) {
      const s = siteById[p.site_id]
      const code = pickDetail(s?.site_details)?.site_code
      return s ? (code ? `${code} — ${s.name}` : s.name) : 'Site ?'
    }
    return '—'
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return personnel.filter(p => {
      if (roleFilter !== 'all' && p.role !== roleFilter) return false
      if (linkFilter === 'linked'   && !p.profile_id) return false
      if (linkFilter === 'unlinked' && p.profile_id)  return false
      if (!q) return true
      const hay = [p.name, p.email, p.phone, p.role, scopeLabel(p)].filter(Boolean).join(' ').toLowerCase()
      return hay.includes(q)
    })
  }, [personnel, search, roleFilter, linkFilter, regionById, districtById, siteById])

  async function saveRow(form) {
    // Validate scope: exactly one of region_id/district_id/site_id must be set
    // based on the chosen scope. The form enforces this but we double-check.
    const payload = {
      role: form.role,
      name: form.name?.trim() || null,
      phone: form.phone?.trim() || null,
      email: form.email?.trim() || null,
      personal_email: form.personal_email?.trim() || null,
      reports_to_id: form.reports_to_id || null,
      profile_id: form.profile_id || null,   // set via "Link to existing user" dropdown
      notes: form.notes?.trim() || null,
      active: form.active !== false,
      region_id:   form.scope === 'region'   ? (form.region_id   || null) : null,
      district_id: form.scope === 'district' ? (form.district_id || null) : null,
      site_id:     form.scope === 'site'     ? (form.site_id     || null) : null,
    }
    if (editing === 'new') {
      const { error } = await supabase.from('site_personnel').insert(payload)
      if (error) { alert('Create failed: ' + error.message); return }
      flash('Person added')
    } else {
      const { error } = await supabase.from('site_personnel').update(payload).eq('id', editing.id)
      if (error) { alert('Save failed: ' + error.message); return }
      flash('Saved')
    }
    setEditing(null)
    load()
  }

  async function deleteRow(row) {
    if (!window.confirm(`Delete ${row.name || row.role}?`)) return
    const { error } = await supabase.from('site_personnel').delete().eq('id', row.id)
    if (error) { alert('Delete failed: ' + error.message); return }
    flash('Deleted')
    load()
  }

  // ------------------------------------------------------------
  // Invite + link flow (Option 3 from our design convo)
  //
  // Flow:
  //   1. Admin clicks "Invite" next to a personnel row.
  //   2. Modal opens pre-filled with the person's name + work email,
  //      and asks what app role they should have (auditor, site_manager,
  //      etc. — unrelated to the org-chart role on the personnel row).
  //   3. On confirm: call the existing admin-users/invite edge function,
  //      which creates the auth user + profile and emails the magic link.
  //   4. Stamp site_personnel.profile_id with the returned user_id.
  //
  // Edge cases:
  //   * Email collision: if the address is already a user, invite fails.
  //     We surface this and offer to link the existing user instead.
  //   * Already linked: the row's "Invite" button hides when profile_id
  //     is set, so this shouldn't fire twice in practice.
  // ------------------------------------------------------------
  async function inviteAndLink({ personnelRow, email, appRole, fullName }) {
    const apiUrl = process.env.REACT_APP_SUPABASE_URL + '/functions/v1/admin-users'
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Not signed in')

    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        action: 'invite',
        email,
        role: appRole,
        full_name: fullName,
        redirect_to: window.location.origin,
      }),
    })
    const text = await res.text()
    let body; try { body = JSON.parse(text) } catch { body = { error: text } }

    if (!res.ok) {
      // Email-already-exists comes through as a 500 from Supabase's
      // inviteUserByEmail. Detect by message content; Supabase doesn't
      // use a stable error code for this.
      const msg = (body.error || '').toLowerCase()
      const alreadyExists = msg.includes('already been registered') || msg.includes('already exists') || msg.includes('user_already_exists')
      if (alreadyExists) {
        // Find the existing profile and offer to link instead.
        const existing = profiles.find(p => (p.email || '').toLowerCase() === email.toLowerCase())
        if (existing && window.confirm(
          `${email} already has an account (${existing.full_name || 'unnamed'}, ${existing.role}). Link this personnel row to that user instead?`
        )) {
          await linkProfile(personnelRow.id, existing.id)
          return
        }
        throw new Error(`A user with ${email} already exists`)
      }
      throw new Error(body.error || `HTTP ${res.status}`)
    }

    // Success — the edge function returned the new user_id. Stamp it on
    // the personnel row so future SiteDetail queries know this person has
    // a login and can be scoped-to.
    if (body.user_id) {
      await linkProfile(personnelRow.id, body.user_id)
    } else {
      // Safety net: if for some reason we didn't get user_id back, still
      // reload so the state is consistent with the server.
      flash(`Invite sent to ${email}, but couldn't auto-link (please link manually)`)
      load()
    }
  }

  async function linkProfile(personnelId, profileId) {
    const { error } = await supabase.from('site_personnel')
      .update({ profile_id: profileId })
      .eq('id', personnelId)
    if (error) { alert('Link failed: ' + error.message); return }
    flash('Invited and linked')
    setInviting(null)
    load()
  }

  async function unlinkProfile(row) {
    if (!window.confirm(`Unlink ${row.name || row.role} from their user account?\n\nThis doesn't delete the user — it just removes the connection between this personnel row and their login.`)) return
    const { error } = await supabase.from('site_personnel').update({ profile_id: null }).eq('id', row.id)
    if (error) { alert('Unlink failed: ' + error.message); return }
    flash('Unlinked')
    load()
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          placeholder="Search name, role, site, email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <select className="form-input" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ width: 150 }}>
          <option value="all">All roles</option>
          {ROLE_PRESETS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="form-input" value={linkFilter} onChange={e => setLinkFilter(e.target.value)} style={{ width: 140 }}>
          <option value="all">All access</option>
          <option value="linked">Has login</option>
          <option value="unlinked">No login</option>
        </select>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          + Add person
        </button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        {filtered.length} of {personnel.length} people
        {' · '}
        {personnel.filter(p => p.profile_id).length} linked to a login
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Scope</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Phone</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Access</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const linkedProfile = p.profile_id ? profileById[p.profile_id] : null
                // "Invite" is only meaningful if we have an email and no
                // existing link. If there's no email we can't send an
                // invite — fall back to the manual link dropdown in Edit.
                const canInvite = !p.profile_id && !!p.email
                return (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border)', opacity: p.active ? 1 : 0.6 }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{p.role}</td>
                  <td style={{ padding: '8px 12px' }}>{p.name || <em style={{ color: 'var(--text-muted)' }}>unnamed</em>}</td>
                  <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>{scopeLabel(p)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 12 }}>{p.phone || '—'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 12 }}>{p.email || '—'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 12 }}>
                    {linkedProfile ? (
                      <span
                        title={`Linked to user ${linkedProfile.full_name || linkedProfile.email || linkedProfile.id.slice(0,8)} (${linkedProfile.role})`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10,
                          background: '#EAF3DE', color: '#3B6D11', fontWeight: 500,
                        }}>
                        ✓ {linkedProfile.role}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>No login</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canInvite && (
                      <button
                        className="btn btn-sm"
                        style={{ color: 'var(--blue)', marginRight: 4 }}
                        onClick={() => setInviting(p)}>
                        Invite
                      </button>
                    )}
                    {linkedProfile && (
                      <button
                        className="btn btn-sm"
                        style={{ color: 'var(--text-muted)', marginRight: 4 }}
                        title="Remove the link between this personnel row and their user account (does not delete the user)"
                        onClick={() => unlinkProfile(p)}>
                        Unlink
                      </button>
                    )}
                    <button className="btn btn-sm" onClick={() => setEditing(p)}>Edit</button>
                    <button className="btn btn-sm" onClick={() => deleteRow(p)} style={{ color: 'var(--red)', marginLeft: 4 }}>Delete</button>
                  </td>
                </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No people match the current filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, background: 'var(--text)', color: 'var(--bg)', padding: '8px 14px', borderRadius: 6, fontSize: 13, zIndex: 200 }}>
          {toast}
        </div>
      )}

      {editing && (
        <PersonnelModal
          data={editing === 'new' ? null : editing}
          regions={regions}
          districts={districts}
          sites={sites}
          personnel={personnel}
          unlinkedProfiles={unlinkedProfiles}
          profileById={profileById}
          onSave={saveRow}
          onClose={() => setEditing(null)}
        />
      )}

      {inviting && (
        <InviteModal
          personnelRow={inviting}
          onSubmit={inviteAndLink}
          onClose={() => setInviting(null)}
        />
      )}
    </div>
  )
}

function PersonnelModal({ data, regions, districts, sites, personnel, unlinkedProfiles, profileById, onSave, onClose }) {
  const isNew = !data
  const [form, setForm] = useState(() => {
    const initialRole = data?.role || 'GM'
    const initialScope = data
      ? (data.region_id ? 'region' : data.district_id ? 'district' : 'site')
      : scopeFor(initialRole)
    return {
      role: initialRole,
      scope: initialScope,
      region_id: data?.region_id || '',
      district_id: data?.district_id || '',
      site_id: data?.site_id || '',
      name: data?.name || '',
      phone: data?.phone || '',
      email: data?.email || '',
      personal_email: data?.personal_email || '',
      reports_to_id: data?.reports_to_id || '',
      profile_id: data?.profile_id || '',
      notes: data?.notes || '',
      active: data?.active !== false,
    }
  })
  const [saving, setSaving] = useState(false)

  function set(key, value) { setForm(p => ({ ...p, [key]: value })) }

  // When role changes, suggest the right scope. Don't force it — if an admin
  // wants to put a custom role at a different level they can, but the
  // common case is that role dictates scope.
  function onRoleChange(newRole) {
    setForm(p => ({ ...p, role: newRole, scope: scopeFor(newRole) }))
  }

  // Filter districts to the chosen region when scope === 'district', and
  // similarly for sites (no per-region filter on sites since site_details
  // has district_id but we don't preload that chain here — flat list is
  // fine for ~127 sites).
  const districtsForScope = districts

  const canSave = form.role && (
    (form.scope === 'region'   && form.region_id)   ||
    (form.scope === 'district' && form.district_id) ||
    (form.scope === 'site'     && form.site_id)
  ) && !saving

  async function handleSave() {
    if (!canSave) return
    setSaving(true)
    try { await onSave(form) } finally { setSaving(false) }
  }

  // People this person might report to: everyone at a higher level.
  // Rough heuristic: RDOs -> nobody, DMs -> RDOs, site-level -> DMs.
  const reportsCandidates = personnel.filter(p => {
    if (data && p.id === data.id) return false  // can't report to yourself
    if (form.role === 'RDO') return false  // top of chain (for now)
    if (form.role === 'DM')  return p.role === 'RDO'
    return p.role === 'DM' || p.role === 'RDO'
  })

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-title">{isNew ? 'Add person' : 'Edit person'}</div>

        <div className="form-group">
          <label className="form-label">Role *</label>
          <input
            className="form-input"
            list="role-presets"
            value={form.role}
            onChange={e => onRoleChange(e.target.value)}
            placeholder="GM, DM, RDO, or custom…"
          />
          <datalist id="role-presets">
            {ROLE_PRESETS.map(r => <option key={r} value={r} />)}
          </datalist>
        </div>

        <div className="form-group">
          <label className="form-label">Scope *</label>
          <select className="form-input" value={form.scope} onChange={e => set('scope', e.target.value)}>
            <option value="region">Region-level (covers all sites in a region)</option>
            <option value="district">District-level (covers all sites in a district)</option>
            <option value="site">Site-level (covers one specific site)</option>
          </select>
        </div>

        {form.scope === 'region' && (
          <div className="form-group">
            <label className="form-label">Region *</label>
            <select className="form-input" value={form.region_id} onChange={e => set('region_id', e.target.value)}>
              <option value="">— Pick a region —</option>
              {regions.map(r => <option key={r.id} value={r.id}>Region {r.code}{r.name ? ` (${r.name})` : ''}</option>)}
            </select>
          </div>
        )}

        {form.scope === 'district' && (
          <div className="form-group">
            <label className="form-label">District *</label>
            <select className="form-input" value={form.district_id} onChange={e => set('district_id', e.target.value)}>
              <option value="">— Pick a district —</option>
              {districtsForScope.map(d => {
                const reg = regions.find(r => r.id === d.region_id)
                return <option key={d.id} value={d.id}>
                  District {d.district_number}{d.coverage_name ? ` — ${d.coverage_name}` : ''} (Region {reg?.code || '?'})
                </option>
              })}
            </select>
          </div>
        )}

        {form.scope === 'site' && (
          <div className="form-group">
            <label className="form-label">Site *</label>
            <select className="form-input" value={form.site_id} onChange={e => set('site_id', e.target.value)}>
              <option value="">— Pick a site —</option>
              {sites.map(s => {
                const code = pickDetail(s.site_details)?.site_code
                return <option key={s.id} value={s.id}>{code ? `${code} — ${s.name}` : s.name}</option>
              })}
            </select>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Name</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Doe" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 555-5555" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@modwash.com" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Personal email</label>
          <input className="form-input" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} placeholder="Optional" />
        </div>

        <div className="form-group">
          <label className="form-label">Reports to</label>
          <select className="form-input" value={form.reports_to_id} onChange={e => set('reports_to_id', e.target.value)}>
            <option value="">— No one —</option>
            {reportsCandidates.map(p => <option key={p.id} value={p.id}>{p.name || p.id.slice(0,8)} ({p.role})</option>)}
          </select>
        </div>

        {/* Link to existing user account.
            Option 1 from the invite-and-link design: when someone already
            has a login (e.g. an admin invited them separately) you can
            manually point this personnel row at their profile here,
            skipping the invite flow entirely. The "Invite" button on the
            table row uses Option 3 (invite + auto-link). */}
        <div className="form-group">
          <label className="form-label">Linked user account</label>
          <select className="form-input" value={form.profile_id} onChange={e => set('profile_id', e.target.value)}>
            <option value="">— Not linked —</option>
            {/* Already-linked profile for this row stays available so we
                don't accidentally unlink by saving. */}
            {form.profile_id && profileById?.[form.profile_id] && !unlinkedProfiles.find(u => u.id === form.profile_id) && (
              <option value={form.profile_id}>
                {profileById[form.profile_id].full_name || profileById[form.profile_id].email} ({profileById[form.profile_id].role}) — currently linked
              </option>
            )}
            {unlinkedProfiles.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.email || u.id.slice(0,8)} — {u.email} ({u.role})
              </option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Only shows users not already linked to another personnel row. To invite a new user, use the Invite button on the list.
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
            Active
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={handleSave}>
            {saving ? 'Saving…' : isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// InviteModal
//
// Small focused modal for the one-click "invite this person" flow.
// Pre-fills everything we already know about the personnel row — their
// name goes into full_name, their work email into email — and only
// asks the admin for the one unknown thing: what app role should this
// person have? (site_manager vs field_tech vs auditor, etc.)
//
// Kept separate from the big edit modal because the cognitive load is
// different: this is "grant login access," not "edit directory info."
// ------------------------------------------------------------
function InviteModal({ personnelRow, onSubmit, onClose }) {
  const [email, setEmail]       = useState(personnelRow.email || '')
  const [fullName, setFullName] = useState(personnelRow.name || '')
  // Default app-role heuristics: the only thing the personnel's org-chart
  // role reliably tells us is "not an admin." Pick a sensible default
  // based on the scope so the admin usually just hits Send.
  const [appRole, setAppRole]   = useState(() => {
    const r = personnelRow.role
    if (r === 'RDO' || r === 'VP') return 'regional_ops'
    if (r === 'DM')                return 'regional_ops'
    if (r === 'GM' || r === 'AGM') return 'site_manager'
    return 'auditor'
  })
  const [sending, setSending]   = useState(false)
  const [error, setError]       = useState(null)

  // Validate: email required, role required. We also block the obvious
  // case of a typo'd email (no @ at all) since the edge function's
  // error message is ugly.
  const canSend = !sending && !!email && email.includes('@') && !!appRole && !!fullName

  async function send() {
    if (!canSend) return
    setSending(true); setError(null)
    try {
      await onSubmit({ personnelRow, email: email.trim(), appRole, fullName: fullName.trim() })
      // Parent closes us on success via setInviting(null).
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-title">Invite {personnelRow.name || 'this person'}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          This will send a magic-link invite to their email and link this personnel row to the new user account.
        </div>

        <div className="form-group">
          <label className="form-label">Full name</label>
          <input className="form-input" value={fullName} onChange={e => setFullName(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Email *</label>
          <input className="form-input" value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="jane@modwash.com" />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            The magic-link invite goes here. They'll land on the app logged in.
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">App role *</label>
          <select className="form-input" value={appRole} onChange={e => setAppRole(e.target.value)}>
            <option value="admin">Admin — full access</option>
            <option value="executive">Executive — cross-program overview</option>
            <option value="regional_ops">Regional Ops — multi-site dispatch</option>
            <option value="site_manager">Site Manager — their site's queue</option>
            <option value="field_tech">Field Tech — dispatched tickets</option>
            <option value="remote_tech">Remote Tech — remote sessions</option>
            <option value="auditor">Auditor — run audits only</option>
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            This is their app role, not their org-chart role ({personnelRow.role}). You can change it later in Users.
          </div>
        </div>

        {error && (
          <div style={{
            padding: 8, borderRadius: 6, marginBottom: 12,
            background: '#FCEBEB', color: '#A32D2D', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSend} onClick={send}>
            {sending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  )
}
